import type { Station, YearRow } from "../lib/types";
import { GRADES } from "../lib/grades";
import { formatValue, plural } from "@core/lib/format";
import ShareCard from "@core/components/ShareCard";

interface Props {
  station: Station;
  rows: YearRow[];
  url: string;
  streamLink: string;
  onClose: () => void;
  onStream: (stream: string) => void;
}

export default function StationDetail({
  station, rows, url, streamLink, onClose, onStream,
}: Props) {
  const g = GRADES[station.grade];
  const trend = station.scoreTrendPerDecade;
  const trendWord =
    trend === null ? null
      // The scale runs 0–100 in steps of 5, so under 3 points a decade is
      // inside the granularity of the scoring itself.
      : Math.abs(trend) < 3 ? "holding steady"
        : trend > 0 ? "improving" : "declining";

  return (
    <aside className="detail" aria-label={`Details for ${station.siteId}`}>
      <header className="detail-head">
        <span className="detail-id">
          <i className="detail-dot" style={{ background: g.color }} />
          {station.siteId}
        </span>
        <button className="icon-btn" onClick={onClose} aria-label="Close site details">
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" fill="none" />
          </svg>
        </button>
      </header>

      <h2 className="detail-name">{station.name}</h2>
      {station.ambiguousId && (
        <p className="detail-warn">
          Station ID <b>{station.siteId}</b> is used by more than one record in
          Airtable. This link addresses this record specifically.
        </p>
      )}
      <p className="detail-sub">
        <a href={streamLink} onClick={(e) => { e.preventDefault(); onStream(station.stream); }}>
          {station.stream}
        </a>
        {` · ${station.watershed} watershed`}
        {station.years > 0 && ` · ${plural(station.years, "year")}`}
      </p>


      <div className="reading" style={{ background: g.soft }}>
        <div className="reading-head">
          <span className="pill" style={{ background: g.color }}>Grade {g.label}</span>
          {station.latest.year && <span className="reading-when">{station.latest.year}</span>}
        </div>
        <div className="reading-label">Total score</div>
        <div className="reading-value" style={{ color: g.color }}>
          {formatValue(station.latest.score, 0)}
          <span className="reading-of">/100</span>
        </div>
        <p className="reading-blurb">{g.blurb}</p>
      </div>

      <dl className="stats">
        <div>
          <dt>Taxa</dt>
          <dd>{formatValue(station.latest.taxa, 1)}</dd>
        </div>
        <div>
          <dt>EPT families</dt>
          <dd>{formatValue(station.latest.ept, 1)}</dd>
        </div>
        <div>
          <dt>P-sensitive</dt>
          <dd>{formatValue(station.latest.psens, 1)}</dd>
        </div>
      </dl>

      {station.location && (
        <p className="detail-sub">Monitoring location: {station.location}</p>
      )}
      {station.comments && <p className="detail-note">{station.comments}</p>}

      {trendWord && (
        <p className="trendline">
          <span aria-hidden="true">📈</span>
          <span>
            Across {plural(rows.length, "sampled year")} this site is <b>{trendWord}</b>
            {trendWord !== "holding steady" && (
              <> — the total score is changing by about{" "}
                <b>{formatValue(Math.abs(trend as number), 0)} points per decade</b></>
            )}.
          </span>
        </p>
      )}

      <ShareCard
        url={url}
        filename={station.key}
        heading="Link to this site"
        blurb="Print the code on a streamside sign — it opens this site's grade and history."
      />
    </aside>
  );
}
